import { ActiveVariantType } from '../types';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { AlternateTranscripts } from './AlternateTranscripts';
import {
  buildTableData as buildCopyData,
  getGeneTextOnly as getCopyNumberGeneTextOnly,
} from '../FilterResultsManager/CopyNumberVariantsContent/buildTableData';
import { buildTableData as buildExpressionData } from '../FilterResultsManager/ExpressionsContent/buildTableData';
import {
  buildTableData as buildShortData,
  getGeneTextOnly as getShortGeneTextOnly,
  getAAChangesTextOnly as getShortAAChangesTextOnly,
} from '../FilterResultsManager/ShortVariantsContent/buildTableData';
import { buildTableData as buildStructData } from '../FilterResultsManager/StructuralVariantsContent/buildTableData';
import { CopyNumberDetails } from './CopyNumberDetails';
import { defineMessages, useIntl } from 'react-intl';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { DrugAssociationsTable } from './DrugAssociationsTable';
import { JaxGeneDrugEvidenceContent } from './JaxGeneDrugEvidence/JaxGeneDrugEvidenceContent';
import { ExpressionDetails } from './ExpressionDetails';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { GeneDetails } from './GeneDetails';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { JaxCancerEffectContent } from './JaxCancerEffect/JaxCancerEffectContent';
import { ShortVariantDetails } from './ShortVariantDetails';
import { StructuralDetails } from './StructuralDetails';
import {
  useActiveAccountId,
  useActiveProjectId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useActiveTest } from '../ActiveTestContext';
import { useSelectedSequenceType } from '../SequenceTypeContext';
import {
  ExpressionVisuals,
  SingleShortVariantNeedlePlot,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
} from './Visualizations';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import * as React from 'react';
import clsx from 'clsx';
import { CancerPrevalenceContent } from './CancerPrevalenceContent';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import mixpanel from 'mixpanel-browser';
import { Events } from '../../../../src/util/mixpanel/Events';
import { getAminoAcidField } from '../utils';

const messages = defineMessages({
  variantDetails: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails',
    defaultMessage: 'Details',
  },
  geneDetails: {
    id: 'omicsExplorer.variantDetails.dialog.geneDetails',
    defaultMessage: 'Gene',
  },
  transcripts: {
    id: 'omicsExplorer.variantDetails.dialog.transcripts',
    defaultMessage: 'Transcripts',
  },
  drugs: {
    id: 'omicsExplorer.variantDetails.dialog.drugs',
    defaultMessage: 'CKB',
  },
  needlePlot: {
    id: 'omicsExplorer.variantDetails.dialog.needlePlot',
    defaultMessage: 'Needle Plot',
  },
  expressionPlots: {
    id: 'omicsExplorer.variantDetails.dialog.expressionPlots',
    defaultMessage: 'Reference Tissue Expression',
  },
  cancerPrevalence: {
    id: 'omicsExplorer.variantDetails.dialog.cancerPrevalence',
    defaultMessage: 'Cancer Prevalence',
  },
  jaxCancerEffect: {
    id: 'omicsExplorer.variantDetails.dialog.jaxCancerEffect',
    defaultMessage: 'JAX Cancer Effect',
  },
  jaxCancerEffectGene: {
    id: 'omicsExplorer.variantDetails.dialog.jaxCancerEffectGene',
    defaultMessage: 'JAX Cancer Effect {gene}',
  },
  jaxGeneDrugEvidence: {
    id: 'omicsExplorer.variantDetails.dialog.jaxGeneDrugEvidence',
    defaultMessage: 'JAX Gene-Drug Evidence',
  },
  jaxGeneDrugEvidenceGene: {
    id: 'omicsExplorer.variantDetails.dialog.jaxGeneDrugEvidenceGene',
    defaultMessage: 'JAX Gene-Drug Evidence {gene}',
  },
  detailsTabs: {
    id: 'omicsExplorer.variantDetails.dialog.detailsTabs',
    defaultMessage: 'Details Tabs',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
  },
  tabList: {
    overflowX: 'auto',
  },
  tab: {
    flex: '0 0 auto',
  },
  tabPanel: {
    height: '94%',
    padding: theme.spacing(4, 0, 0),
  },
  directionRow: {
    flexDirection: 'row',
  },
  tableContainer: {
    width: '100%',
    overflow: 'auto',
    maxHeight: `calc(100vh - ${theme.pxToRem(200)})`,
  },
  splitContainer: {
    display: 'flex',
  },
  splitContainerCol: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  emptyStateRoot: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
}));

interface VariantDetailsProps {
  data?: any;
  activeSequenceFromProps?: any;
  variantType?: ActiveVariantType;
  useSequenceTypeFromData?: boolean;
}

export const VariantDetails: React.FC<VariantDetailsProps> = ({
  activeSequenceFromProps,
  data,
  variantType,
  useSequenceTypeFromData = false,
}) => {
  const [selectedTab, setSelectedTab] = React.useState('details');

  const hasFeature = useFeatures();
  const hasJaxOETabs = hasFeature('jaxOETabs');

  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [activeTest] = useActiveTest();
  const [selectedSequenceType] = useSelectedSequenceType();
  const activeAccountId = useActiveAccountId();
  const aminoAcidField = getAminoAcidField(activeAccountId);
  const activeProjectId = useActiveProjectId();

  if (!data) {
    return null;
  }

  const sequence = activeTest || activeSequenceFromProps;

  const cols =
    variantType === 'short'
      ? buildShortData({
          variantSet: data,
          activeSequence: sequence,
          account: activeAccountId,
          build: sequence.referenceSetId,
          formatMessage,
        })
      : variantType === 'copyNumber'
      ? buildCopyData({ build: sequence.referenceSetId, formatMessage })
      : variantType === 'structural'
      ? buildStructData({ build: sequence.referenceSetId, formatMessage })
      : variantType === 'expression'
      ? buildExpressionData({
          account: activeAccountId,
          activeSequence: sequence,
          build: sequence.referenceSetId,
          formatMessage,
        })
      : null;

  if (!cols) {
    return null;
  }

  // If we have a decent amount of columns, split them in half so
  // we can display them in a two-column format
  let firstHalf = null;
  let secondHalf = null;
  if (cols.length > 8) {
    const idx = Math.floor(cols.length / 2);
    firstHalf = cols.slice(0, idx);
    secondHalf = cols.slice(idx, cols.length);
  }

  const gene: string | Array<string> =
    variantType === 'short'
      ? getShortGeneTextOnly(data)
      : variantType === 'copyNumber'
      ? getCopyNumberGeneTextOnly(data)
      : [...new Set([data.geneId1, data.geneId2].filter(Boolean))];
  const aaChanges: string[] = getShortAAChangesTextOnly(data, aminoAcidField);

  const handleOnTabClick = (tab: any, index?: number) => {
    setSelectedTab(typeof index === 'number' ? `${tab}${index}` : tab);

    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    mixpanel.track(Events.omicsExplorer.variantDetailTabs[tab], {
      accountId: activeAccountId,
      gene: data?.gene || gene,
      projectId: activeProjectId,
      variantType,
    });
  };

  return (
    <div className={classes.root}>
      <Tabs selectedTabId={selectedTab}>
        <TabList
          aria-label={formatMessage(messages.detailsTabs)}
          className={classes.tabList}
        >
          <Tab
            className={classes.tab}
            onClick={() => handleOnTabClick('details')}
            stopId="details"
          >
            {formatMessage(messages.variantDetails)}
          </Tab>

          <Tab
            className={classes.tab}
            onClick={() => handleOnTabClick('gene')}
            stopId="gene"
          >
            {formatMessage(messages.geneDetails)}
          </Tab>

          {variantType === 'short' && (
            <Tab
              className={classes.tab}
              onClick={() => handleOnTabClick('transcripts')}
              stopId="transcripts"
            >
              {formatMessage(messages.transcripts)}
            </Tab>
          )}

          {variantType === 'short' && (
            <Tab
              className={classes.tab}
              onClick={() => handleOnTabClick('needlePlot')}
              stopId="needlePlot"
            >
              {formatMessage(messages.needlePlot)}
            </Tab>
          )}

          {hasJaxOETabs &&
            (variantType === 'short' ||
              (variantType === 'structural' && gene.length === 1)) && (
              <Tab
                className={classes.tab}
                onClick={() => handleOnTabClick('jaxCancerEffect')}
                stopId="jaxCancerEffect"
              >
                {formatMessage(messages.jaxCancerEffect)}
              </Tab>
            )}

          {hasJaxOETabs &&
            variantType === 'structural' &&
            gene.length > 1 &&
            (gene as Array<string>).map((g: string, index: number) => (
              <Tab
                className={classes.tab}
                key={`jaxCancerEffect${index}`}
                onClick={() => handleOnTabClick('jaxCancerEffect', index)}
                stopId={`jaxCancerEffect${index}`}
              >
                {formatMessage(messages.jaxCancerEffectGene, {
                  gene: g,
                })}
              </Tab>
            ))}

          {hasJaxOETabs &&
            (variantType === 'short' ||
              variantType === 'copyNumber' ||
              (variantType === 'structural' && gene.length === 1)) && (
              <Tab
                className={classes.tab}
                onClick={() => handleOnTabClick('jaxGeneDrugEvidence')}
                stopId="jaxGeneDrugEvidence"
              >
                {formatMessage(messages.jaxGeneDrugEvidence)}
              </Tab>
            )}

          {hasJaxOETabs &&
            variantType === 'structural' &&
            gene.length > 1 &&
            (gene as Array<string>).map((g: string, index: number) => (
              <Tab
                className={classes.tab}
                key={`jaxGeneDrugEvidence${index}`}
                onClick={() => handleOnTabClick('jaxGeneDrugEvidence', index)}
                stopId={`jaxGeneDrugEvidence${index}`}
              >
                {formatMessage(messages.jaxGeneDrugEvidenceGene, { gene: g })}
              </Tab>
            ))}

          {variantType === 'expression' && (
            <Tab
              className={classes.tab}
              onClick={() => handleOnTabClick('expressionPlots')}
              stopId="expressionPlots"
            >
              {formatMessage(messages.expressionPlots)}
            </Tab>
          )}

          <Tab
            className={classes.tab}
            onClick={() => handleOnTabClick('drugs')}
            stopId="drugs"
          >
            {formatMessage(messages.drugs)}
          </Tab>

          {variantType !== 'expression' && (
            <Tab
              className={classes.tab}
              onClick={() => handleOnTabClick('cancerPrevalence')}
              stopId="cancerPrevalence"
            >
              {formatMessage(messages.cancerPrevalence)}
            </Tab>
          )}
        </TabList>

        <TabPanel
          stopId="details"
          className={clsx(
            classes.tabPanel,
            firstHalf && secondHalf && classes.splitContainer,
            {
              [classes.directionRow]: firstHalf && secondHalf,
            },
          )}
        >
          {selectedTab === 'details' && (
            <>
              {variantType === 'short' ? (
                <ShortVariantDetails
                  account={activeAccountId}
                  activeSequence={sequence}
                  build={sequence.referenceSetId}
                  variant={data}
                  variantSet={data}
                />
              ) : variantType === 'copyNumber' ? (
                <CopyNumberDetails
                  build={sequence.referenceSetId}
                  variant={data}
                />
              ) : variantType === 'structural' ? (
                <StructuralDetails
                  build={sequence.referenceSetId}
                  variant={data}
                />
              ) : variantType === 'expression' ? (
                <ExpressionDetails
                  account={activeAccountId}
                  activeSequence={sequence}
                  variant={data}
                />
              ) : null}
            </>
          )}
        </TabPanel>

        <TabPanel stopId="gene" className={classes.tabPanel}>
          {selectedTab === 'gene' && (
            <GeneDetails
              data={data}
              contentClassName={classes.tableContainer}
              emptyStateClassName={classes.emptyStateRoot}
            />
          )}
        </TabPanel>

        {variantType === 'short' && (
          <TabPanel stopId="transcripts" className={classes.tabPanel}>
            {selectedTab === 'transcripts' && (
              <AlternateTranscripts
                contentClassName={classes.tableContainer}
                emptyStateClassName={classes.emptyStateRoot}
                data={data}
                variantType={variantType}
              />
            )}
          </TabPanel>
        )}

        {variantType === 'short' && (
          <TabPanel stopId="needlePlot" className={classes.tabPanel}>
            {selectedTab === 'needlePlot' && (
              <SingleShortVariantNeedlePlot
                transcripts={data?.ensemblCanon || data?.ensembl}
                transcriptId={
                  data?.ensemblCanon?.[0]?.transcriptId ||
                  data?.ensembl?.[0]?.transcriptId
                }
                selectedSequenceType={
                  useSequenceTypeFromData
                    ? data?.sequenceType
                    : selectedSequenceType
                }
              />
            )}
          </TabPanel>
        )}

        {hasJaxOETabs &&
          (variantType === 'short' ||
            (variantType === 'structural' && gene.length === 1)) && (
            <TabPanel stopId="jaxCancerEffect" className={classes.tabPanel}>
              {selectedTab === 'jaxCancerEffect' && (
                <JaxCancerEffectContent
                  gene={gene as string}
                  aaChanges={aaChanges}
                />
              )}
            </TabPanel>
          )}

        {hasJaxOETabs &&
          variantType === 'structural' &&
          gene.length > 1 &&
          (gene as Array<string>).map((g: string, index: number) => (
            <TabPanel
              className={classes.tabPanel}
              key={`jaxCancerEffect${index}`}
              stopId={`jaxCancerEffect${index}`}
            >
              {selectedTab === `jaxCancerEffect${index}` && (
                <JaxCancerEffectContent gene={g} />
              )}
            </TabPanel>
          ))}

        {hasJaxOETabs &&
          (variantType === 'short' ||
            variantType === 'copyNumber' ||
            (variantType === 'structural' && gene.length === 1)) && (
            <TabPanel stopId="jaxGeneDrugEvidence" className={classes.tabPanel}>
              {selectedTab === 'jaxGeneDrugEvidence' && (
                <JaxGeneDrugEvidenceContent gene={gene as string} />
              )}
            </TabPanel>
          )}

        {hasJaxOETabs &&
          variantType === 'structural' &&
          gene.length > 1 &&
          (gene as Array<string>).map((g: string, index: number) => (
            <TabPanel
              className={classes.tabPanel}
              key={`jaxGeneDrugEvidence${index}`}
              stopId={`jaxGeneDrugEvidence${index}`}
            >
              {selectedTab === `jaxGeneDrugEvidence${index}` && (
                <JaxGeneDrugEvidenceContent gene={g} />
              )}
            </TabPanel>
          ))}

        {variantType === 'expression' && (
          <TabPanel stopId="expressionPlots" className={classes.tabPanel}>
            {selectedTab === 'expressionPlots' && (
              <ExpressionVisuals
                gene={data.gene}
                expression={data.expression}
              />
            )}
          </TabPanel>
        )}

        <TabPanel stopId="drugs" className={classes.tabPanel}>
          {selectedTab === 'drugs' && (
            <DrugAssociationsTable
              contentClassName={classes.tableContainer}
              emptyStateClassName={classes.emptyStateRoot}
              data={{ ...data }}
              variantType={variantType}
            />
          )}
        </TabPanel>

        {variantType !== 'expression' && (
          <TabPanel stopId="cancerPrevalence" className={classes.tabPanel}>
            {selectedTab === 'cancerPrevalence' && (
              <CancerPrevalenceContent
                data={data}
                contentClassName={classes.tableContainer}
                emptyStateClassName={classes.emptyStateRoot}
              />
            )}
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
};
